import { PlaceType } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Map as MlMap } from 'maplibre-gl';
import { Circle, Hexagon, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Can } from '../../app/guards';
import { Badge, EmptyState, Field, Skeleton, Spinner, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { DrawPlace, type DrawState } from '../../map/DrawPlace';
import { GeoSearch } from '../../map/GeoSearch';
import { MapView } from '../../map/MapView';
import { PlacesLayer, type PlaceGeom } from '../../map/PlacesLayer';

interface Place extends PlaceGeom {
  address: string | null;
  customerId: string | null;
  customer: { id: string; name: string } | null;
  isActive: boolean;
}
interface CustomerLite { id: string; name: string }

type Editor = { mode: 'new' | 'edit'; id?: string; name: string; type: string; customerId: string; address: string; color: string; draw: DrawState };

const PALETTE = ['#14532D', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2'];
const emptyEditor = (shape: 'CIRCLE' | 'POLYGON'): Editor => ({ mode: 'new', name: '', type: 'CUSTOMER', customerId: '', address: '', color: PALETTE[0]!, draw: shape === 'CIRCLE' ? { shape, center: null, radiusM: 200 } : { shape, points: [] } });

export function PlacesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const can = useSession((s) => s.can);
  const [params, setParams] = useSearchParams();
  const [selected, setSelected] = useState<string | null>(params.get('focus'));
  const [editor, setEditor] = useState<Editor | null>(null);
  const [filter, setFilter] = useState('');
  const mapRef = useRef<MlMap | null>(null);

  const q = useQuery({ queryKey: ['places-all'], queryFn: () => api<Place[]>('/places/all') });
  const customers = useQuery({ queryKey: ['customers-lite'], queryFn: () => api<{ data: CustomerLite[] }>('/customers?pageSize=200').then((r) => r.data) });
  const places = useMemo(() => (q.data ?? []).filter((p) => !filter || p.name.includes(filter) || (p.address ?? '').includes(filter)), [q.data, filter]);
  const sel = places.find((p) => p.id === selected) ?? null;

  const flyTo = useCallback((p: Place) => {
    const m = mapRef.current;
    if (!m) return;
    m.flyTo({ center: [p.centerLon, p.centerLat], zoom: p.shape === 'CIRCLE' ? Math.max(12, 16 - Math.log2((p.radiusM ?? 200) / 100)) : 13, duration: 600 });
  }, []);
  useEffect(() => {
    if (sel) flyTo(sel);
  }, [sel, flyTo]);

  const onMapClick = useCallback((id: string) => {
    setSelected(id);
    setParams({ focus: id }, { replace: true });
  }, [setParams]);

  const save = useMutation({
    mutationFn: async (ed: Editor) => {
      const d = ed.draw;
      let center: [number, number];
      let body: Record<string, unknown>;
      if (d.shape === 'CIRCLE') {
        if (!d.center) throw new Error(t('places.needCenter'));
        center = d.center;
        body = { shape: 'CIRCLE', centerLon: center[0], centerLat: center[1], radiusM: d.radiusM };
      } else {
        if (d.points.length < 3) throw new Error(t('places.needPoints'));
        center = [d.points.reduce((s, p) => s + p[0], 0) / d.points.length, d.points.reduce((s, p) => s + p[1], 0) / d.points.length];
        body = { shape: 'POLYGON', centerLon: center[0], centerLat: center[1], polygon: [[...d.points, d.points[0]]] };
      }
      const json = { ...body, name: ed.name, type: ed.type, customerId: ed.customerId || null, address: ed.address || undefined, color: ed.color, isActive: true };
      return ed.mode === 'edit' ? api<Place>(`/places/${ed.id}`, { method: 'PATCH', json }) : api<Place>('/places', { method: 'POST', json });
    },
    onSuccess: (p) => { toast.success(t('common.saved')); setEditor(null); setSelected(p.id); void qc.invalidateQueries({ queryKey: ['places-all'] }); },
    onError: toast.error,
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/places/${id}`, { method: 'DELETE' }), onSuccess: () => { toast.success(t('common.saved')); setSelected(null); void qc.invalidateQueries({ queryKey: ['places-all'] }); }, onError: toast.error });

  const startEdit = (p: Place) => {
    setEditor({
      mode: 'edit', id: p.id, name: p.name, type: p.type, customerId: p.customerId ?? '', address: p.address ?? '', color: p.color ?? PALETTE[0]!,
      draw: p.shape === 'CIRCLE' ? { shape: 'CIRCLE', center: [p.centerLon, p.centerLat], radiusM: p.radiusM ?? 200 } : { shape: 'POLYGON', points: (p.polygon?.[0] ?? []).slice(0, -1) as [number, number][] },
    });
    setSelected(null);
  };
  const onDraw = useCallback((s: DrawState) => setEditor((e) => (e ? { ...e, draw: s } : e)), []);

  // New place with no address yet → suggest one from the drawn centre (reverse geocode, best-effort).
  const centre = editor?.mode === 'new' && !editor.address ? drawCentre(editor.draw) : null;
  const centreKey = centre ? `${centre[0].toFixed(4)},${centre[1].toFixed(4)}` : null;
  useEffect(() => {
    if (!centreKey) return;
    const [lon, lat] = centreKey.split(',').map(Number) as [number, number];
    let stale = false;
    api<{ address: string | null }>(`/geo/reverse?lat=${lat}&lon=${lon}`)
      .then((r) => { if (!stale && r.address) setEditor((e) => (e && e.mode === 'new' && !e.address ? { ...e, address: r.address! } : e)); })
      .catch(() => undefined);
    return () => { stale = true; };
  }, [centreKey]);

  return (
    <div className="flex h-[calc(100dvh-140px)] min-h-[520px] flex-col gap-3 lg:flex-row">
      <aside className="card flex w-full flex-col overflow-hidden lg:w-[340px] lg:flex-none">
        <div className="flex items-center gap-2 border-b border-border-soft p-3">
          <input className="input" placeholder={t('common.search')} value={filter} onChange={(e) => setFilter(e.target.value)} aria-label={t('common.search')} />
          {can('places:create') && !editor ? (
            <div className="flex gap-1">
              <button className="btn-primary !px-2.5" title={t('places.addCircle')} aria-label={t('places.addCircle')} onClick={() => { setSelected(null); setEditor(emptyEditor('CIRCLE')); }}><Plus size={16} /><Circle size={14} /></button>
              <button className="btn-secondary !px-2.5" title={t('places.addPolygon')} aria-label={t('places.addPolygon')} onClick={() => { setSelected(null); setEditor(emptyEditor('POLYGON')); }}><Plus size={16} /><Hexagon size={14} /></button>
            </div>
          ) : null}
        </div>

        {editor ? (
          <form className="flex flex-1 flex-col gap-3 overflow-y-auto p-3" onSubmit={(e) => { e.preventDefault(); save.mutate(editor); }}>
            <div className="flex items-center justify-between"><h3 className="font-bold">{editor.mode === 'edit' ? t('places.edit') : t('places.add')}</h3><button type="button" className="btn-ghost size-9 !p-0" aria-label={t('common.cancel')} onClick={() => setEditor(null)}><X size={16} /></button></div>
            <p className="rounded-md bg-info-bg px-3 py-2 text-xs text-info-fg">{editor.draw.shape === 'CIRCLE' ? t('places.hintCircle') : t('places.hintPolygon', { n: editor.draw.points.length })}</p>
            <Field label={t('places.name')} required>{(id) => <input id={id} className="input" value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} required />}</Field>
            <Field label={t('places.type')}>{(id) => <select id={id} className="input" value={editor.type} onChange={(e) => setEditor({ ...editor, type: e.target.value })}>{PlaceType.map((x) => <option key={x} value={x}>{t(`places.types.${x}`)}</option>)}</select>}</Field>
            <Field label={t('customers.title')}>{(id) => <select id={id} className="input" value={editor.customerId} onChange={(e) => setEditor({ ...editor, customerId: e.target.value })}><option value="">—</option>{customers.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>}</Field>
            {editor.draw.shape === 'CIRCLE' ? (
              <RadiusField draw={editor.draw} onChange={(d) => setEditor({ ...editor, draw: d })} />
            ) : (
              <button type="button" className="btn-secondary" disabled={!editor.draw.points.length} onClick={() => setEditor({ ...editor, draw: { shape: 'POLYGON', points: (editor.draw as { points: [number, number][] }).points.slice(0, -1) } })}>{t('places.undoPoint')}</button>
            )}
            <Field label={t('customers.address')}>{(id) => <input id={id} className="input" value={editor.address} onChange={(e) => setEditor({ ...editor, address: e.target.value })} />}</Field>
            <div><span className="label">{t('vehicles.color')}</span><div className="flex gap-2">{PALETTE.map((c) => <button key={c} type="button" aria-label={c} className={`size-7 rounded-full border-2 ${editor.color === c ? 'border-text' : 'border-transparent'}`} style={{ background: c }} onClick={() => setEditor({ ...editor, color: c })} />)}</div></div>
            <div className="mt-auto flex gap-2"><button type="button" className="btn-secondary flex-1" onClick={() => setEditor(null)}>{t('common.cancel')}</button><button type="submit" className="btn-primary flex-1" disabled={save.isPending}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></div>
          </form>
        ) : (
          <ul className="flex-1 overflow-y-auto">
            {q.isLoading ? <li className="p-3"><Skeleton className="h-40" /></li> : !places.length ? <li><EmptyState title={t('places.empty')} hint={t('places.emptyHint')} /></li> : places.map((p) => (
              <li key={p.id}>
                <button className={`flex w-full items-start gap-3 border-b border-border-soft px-3 py-2.5 text-start hover:bg-surface-hover ${selected === p.id ? 'bg-brand-soft' : ''}`} onClick={() => onMapClick(p.id)}>
                  <span className="mt-1 size-3 flex-none rounded-full" style={{ background: p.color ?? '#14532D' }} />
                  <span className="min-w-0 flex-1"><span className="block truncate text-sm font-semibold">{p.name}</span><span className="block truncate text-xs text-text-muted">{t(`places.types.${p.type}`)}{p.customer ? ` · ${p.customer.name}` : ''}{p.shape === 'CIRCLE' ? ` · ${p.radiusM} ${t('units.m')}` : ''}</span></span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div className="card relative min-h-[320px] flex-1 overflow-hidden">
        <MapView zoom={6} onReady={(m) => { mapRef.current = m; if (sel) flyTo(sel); }}>
          <PlacesLayer places={editor?.mode === 'edit' ? places.filter((p) => p.id !== editor.id) : places} selectedId={selected} onClick={editor ? undefined : onMapClick} />
          {editor ? <DrawPlace state={editor.draw} onChange={onDraw} /> : null}
          <GeoSearch onPick={(h) => setEditor((e) => (e && !e.address ? { ...e, address: h.label } : e))} />
        </MapView>
        {sel && !editor ? (
          <div className="absolute inset-x-3 bottom-3 flex items-center gap-3 rounded-lg border border-border bg-surface/95 px-4 py-3 shadow-overlay">
            <span className="size-3 rounded-full" style={{ background: sel.color ?? '#14532D' }} />
            <div className="min-w-0 flex-1"><div className="truncate font-bold">{sel.name}</div><div className="truncate text-xs text-text-muted">{sel.address ?? t(`places.types.${sel.type}`)}</div></div>
            <Badge tone="brand">{t(`places.types.${sel.type}`)}</Badge>
            <Can perm="places:update"><button className="btn-ghost size-9 !p-0" aria-label={t('common.edit')} onClick={() => startEdit(sel)}><Pencil size={16} /></button></Can>
            <Can perm="places:delete"><button className="btn-ghost size-9 !p-0 text-danger-fg" aria-label={t('common.delete')} onClick={() => confirmDialog(t('places.deleteConfirm', { name: sel.name }), { danger: true }).then((ok) => ok && remove.mutate(sel.id))}><Trash2 size={16} /></button></Can>
            <button className="btn-ghost size-9 !p-0" aria-label={t('common.cancel')} onClick={() => setSelected(null)}><X size={16} /></button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** [lon, lat] of the drawn shape: circle centre or polygon vertex average (needs ≥3 points). */
function drawCentre(d: DrawState): [number, number] | null {
  if (d.shape === 'CIRCLE') return d.center;
  if (d.points.length < 3) return null;
  const n = d.points.length;
  return [d.points.reduce((a, p) => a + p[0], 0) / n, d.points.reduce((a, p) => a + p[1], 0) / n];
}

function RadiusField({ draw, onChange }: { draw: Extract<DrawState, { shape: 'CIRCLE' }>; onChange: (d: DrawState) => void }) {
  const { t } = useTranslation();
  return (
    <Field label={`${t('places.radius')}: ${draw.radiusM} ${t('units.m')}`}>
      {(id) => <input id={id} type="range" min={20} max={5000} step={10} className="w-full accent-brand" value={draw.radiusM} onChange={(e) => onChange({ ...draw, radiusM: Number(e.target.value) })} />}
    </Field>
  );
}
